"use client";

import React, { useEffect, useMemo, useState } from "react";
import { FiArrowLeft, FiArrowRight, FiCalendar, FiCheck, FiClock, FiMapPin } from "react-icons/fi";
import { getPublicSchoolCalendar } from "@/api/schoolCalendarService";
import { useSchoolClassOptions } from "@/components/enquiryForm/cxcRegistration/useSchoolClassOptions";
import { SchoolCalendarItem } from "@/models/api/schoolCalendar";
import styles from "./SchoolCalendar.module.css";

const ALL_CATEGORIES = "All";
const ALL_CLASSES = "All classes";
const BASE_CATEGORIES = ["Event", "Class Event", "Exam Dates", "Holiday"];
// One colour per class, so class events are told apart by class (as on the school's printed calendar key).
const DOT_COLORS: Record<string, string> = { Event: "#196565", Holiday: "#ef4444", "Exam Dates": "#d4a900" };
const CLASS_COLORS = ["#2563eb", "#7c3aed", "#db2777", "#ea580c", "#0d9488", "#65a30d", "#0891b2", "#b45309", "#4f46e5", "#be123c", "#0f766e", "#6d28d9"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const CATEGORY_CLASS: Record<string, string> = {
  Event: styles.catEvent,
  Holiday: styles.catHoliday,
  "Class Event": styles.catClass,
  "Exam Dates": styles.catExam,
};

// yyyy-MM-dd -> local Date, without the timezone shift that new Date("yyyy-MM-dd") causes.
const parseDay = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const pad2 = (n: number) => String(n).padStart(2, "0");
const monthKey = (d: Date) => d.getFullYear() * 12 + d.getMonth();

const endOf = (item: SchoolCalendarItem) => parseDay(item.endDate || item.date);
const isMultiDay = (item: SchoolCalendarItem) => !!item.endDate && item.endDate !== item.date;

const timeLabel = (item: SchoolCalendarItem) => {
  if (item.isAllDay || !item.startTime) return "";
  return item.endTime ? `${item.startTime} - ${item.endTime}` : item.startTime;
};

// Class events and exams get their own categories so they can be filtered on their own.
const categoryOf = (item: SchoolCalendarItem) =>
  item.sourceType === "ClassEvent" ? "Class Event" : item.sourceType === "Exam" ? "Exam Dates" : item.category;

/* ---------- Add to calendar (.ics) ---------- */
const icsEscape = (s: string) => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
const icsDate = (d: Date) => `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;

const buildEvent = (item: SchoolCalendarItem) => {
  const start = parseDay(item.date);
  const lines = ["BEGIN:VEVENT", `UID:${item.sourceType}-${item.id}-${item.date}@school-calendar`, `DTSTAMP:${icsDate(new Date())}T000000Z`];
  if (item.isAllDay || !item.startTime) {
    const endExclusive = endOf(item);
    endExclusive.setDate(endExclusive.getDate() + 1);
    lines.push(`DTSTART;VALUE=DATE:${icsDate(start)}`, `DTEND;VALUE=DATE:${icsDate(endExclusive)}`);
  } else {
    const [sh, sm] = item.startTime.split(":");
    const [eh, em] = (item.endTime || item.startTime).split(":");
    lines.push(`DTSTART:${icsDate(start)}T${sh}${sm}00`, `DTEND:${icsDate(endOf(item))}T${eh}${em}00`);
  }
  lines.push(`SUMMARY:${icsEscape(item.title)}`);
  if (item.description) lines.push(`DESCRIPTION:${icsEscape(item.description)}`);
  if (item.location) lines.push(`LOCATION:${icsEscape(item.location)}`);
  lines.push("END:VEVENT");
  return lines.join("\r\n");
};

const downloadIcs = (items: SchoolCalendarItem[], fileName: string) => {
  const body = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//School Calendar//EN", "CALSCALE:GREGORIAN", ...items.map(buildEvent), "END:VCALENDAR"].join("\r\n");
  const url = URL.createObjectURL(new Blob([body], { type: "text/calendar;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/* ---------- Page ---------- */
const SchoolCalendar: React.FC = () => {
  const today = useMemo(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  }, []);

  const [items, setItems] = useState<SchoolCalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [view, setView] = useState<"all" | "month">("month");
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [classFilter, setClassFilter] = useState(ALL_CLASSES);
  const schoolClasses = useSchoolClassOptions();
  // The calendar only ever covers the current calendar year (Jan-Dec).
  const year = today.getFullYear();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    const from = new Date(year, 0, 1);
    const to = new Date(year, 11, 31);
    getPublicSchoolCalendar(toIso(from), toIso(to))
      .then(data => { if (active) setItems(data); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [year, retry]);

  const categories = useMemo(
    () => [ALL_CATEGORIES, ...BASE_CATEGORIES, ...Array.from(new Set(items.map(categoryOf))).filter(c => !BASE_CATEGORIES.includes(c)).sort()],
    [items]
  );

  // Every school class (so a parent can pick theirs even before it has an entry), then any other class name found on an entry.
  const classNames = useMemo(() => {
    const names = schoolClasses.map(c => c.value);
    const extra = Array.from(new Set(items.map(i => i.className).filter((c): c is string => !!c))).filter(c => !names.includes(c)).sort();
    return [ALL_CLASSES, ...names, ...extra];
  }, [items, schoolClasses]);

  const classColor = (name?: string | null) => {
    const i = name ? classNames.indexOf(name) : -1;
    return i > 0 ? CLASS_COLORS[(i - 1) % CLASS_COLORS.length] : undefined;
  };

  // A class filter keeps that class's entries plus everything that is for the whole school.
  const filtered = useMemo(
    () => items
      .filter(i =>
        (category === ALL_CATEGORIES || categoryOf(i) === category) &&
        (classFilter === ALL_CLASSES || !i.className || i.className === classFilter || i.className.startsWith(classFilter + " ")))
      .sort((a, b) => a.date.localeCompare(b.date) || (a.startTime || "").localeCompare(b.startTime || "")),
    [items, category, classFilter]
  );

  const visibleMonth = useMemo(() => new Date(year, viewMonth, 1), [year, viewMonth]);

  // Days of the visible month that have at least one entry, for the dots on the small calendar.
  const daysWithEntries = useMemo(() => {
    const set = new Map<number, string>();
    const monthEnd = new Date(year, viewMonth + 1, 0);
    filtered.forEach(i => {
      const s = parseDay(i.date);
      const e = endOf(i);
      if (e < visibleMonth || s > monthEnd) return;
      for (let d = new Date(Math.max(s.getTime(), visibleMonth.getTime())); d <= e && d <= monthEnd; d.setDate(d.getDate() + 1)) {
        // The dot takes the class's colour for a class event, else the category's colour (first entry of the day wins).
        if (!set.has(d.getDate())) set.set(d.getDate(), (i.sourceType === "ClassEvent" ? classColor(i.className) : undefined) || DOT_COLORS[categoryOf(i)] || "#196565");
      }
    });
    return set;
  }, [filtered, visibleMonth, year, viewMonth, classNames]);

  // A clicked day only counts while it still has entries under the current filters.
  const activeDay = selectedDay !== null && daysWithEntries.has(selectedDay) ? selectedDay : null;

  useEffect(() => { setSelectedDay(null); }, [view, category, classFilter]);

  const goToMonth = (m: number) => { setViewMonth(Math.min(11, Math.max(0, m))); setSelectedDay(null); };

  // Items shown right now, grouped under a "Month Year" heading.
  const groups = useMemo(() => {
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const shown = view === "all"
      ? filtered.filter(i => endOf(i) >= thisMonthStart)
      : filtered.filter(i => {
          const s = parseDay(i.date);
          const e = endOf(i);
          if (!(monthKey(s) <= monthKey(visibleMonth) && monthKey(e) >= monthKey(visibleMonth))) return false;
          if (activeDay === null) return true;
          const day = new Date(year, viewMonth, activeDay);
          return s <= day && e >= day;
        });
    const map = new Map<string, SchoolCalendarItem[]>();
    shown.forEach(i => {
      const d = parseDay(i.date);
      if (d.getFullYear() < year) d.setFullYear(year, 0, 1);
      if (view === "all" && d < thisMonthStart) d.setTime(thisMonthStart.getTime());
      const key = view === "month" ? `${visibleMonth.getFullYear()}-${visibleMonth.getMonth()}` : `${d.getFullYear()}-${d.getMonth()}`;
      map.set(key, [...(map.get(key) || []), i]);
    });
    return Array.from(map.entries()).map(([key, list]) => {
      const [y, m] = key.split("-").map(Number);
      return { key, label: view === "month" ? `Events in ${MONTH_NAMES[m]} ${y}` : `${MONTH_NAMES[m]} ${y}`, list };
    });
  }, [filtered, view, today, visibleMonth, activeDay, year, viewMonth]);

  const hasAny = groups.length > 0;

  return (
    <section className={styles.schoolCalendarArea}>
      <div className="container">
        <div className="row">
          {/* ---------- Left: filters, month calendar, subscribe, colour key ---------- */}
          <div className="col-lg-4 mb-4">
            <label className={styles.barRow}>
              <span>Category:</span>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                {categories.map(c => <option key={c} value={c}>{c === ALL_CATEGORIES ? "- View All -" : c}</option>)}
              </select>
            </label>

            {classNames.length > 1 && (
              <label className={`${styles.barRow} ${styles.barRowGap}`}>
                <span>Class:</span>
                <select value={classFilter} onChange={e => setClassFilter(e.target.value)}>
                  {classNames.map(c => <option key={c} value={c}>{c === ALL_CLASSES ? "- All classes -" : c}</option>)}
                </select>
              </label>
            )}

            {view === "month" && (
              <div className={styles.miniCal}>
                <div className={styles.monthBar}>
                  <button type="button" aria-label="Previous month" disabled={viewMonth === 0} onClick={() => goToMonth(viewMonth - 1)}><FiArrowLeft /></button>
                  <span>{MONTH_NAMES[viewMonth]} {year}</span>
                  <button type="button" aria-label="Next month" disabled={viewMonth === 11} onClick={() => goToMonth(viewMonth + 1)}><FiArrowRight /></button>
                </div>
                <div className={styles.miniGrid}>
                  {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <span key={i} className={styles.miniDow}>{d}</span>)}
                  {Array.from({ length: visibleMonth.getDay() }).map((_, i) => <span key={`b${i}`} className={styles.miniBlank} />)}
                  {Array.from({ length: new Date(year, viewMonth + 1, 0).getDate() }).map((_, i) => {
                    const day = i + 1;
                    const has = daysWithEntries.has(day);
                    const date = new Date(year, viewMonth, day);
                    const isToday = date.getTime() === today.getTime();
                    return (
                      <button
                        key={day}
                        type="button"
                        disabled={!has}
                        style={has ? ({ "--dot": daysWithEntries.get(day) } as React.CSSProperties) : undefined}
                        title={has ? "Show this day" : undefined}
                        className={`${styles.miniDay} ${has ? styles.miniHas : ""} ${date < today ? styles.miniPast : ""} ${isToday ? styles.miniToday : ""} ${activeDay === day ? styles.miniSelected : ""}`}
                        onClick={() => setSelectedDay(activeDay === day ? null : day)}
                      >
                        {pad2(day)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              type="button"
              className={styles.subscribeLink}
              disabled={filtered.length === 0}
              onClick={() => downloadIcs(filtered, "school-calendar.ics")}
            >
              <FiCalendar /> <span>Subscribe to full calendar</span>
            </button>

            <div className={styles.thickRule} />

            {/* Colour key: what each colour means; every class has its own colour. */}
            <div className={styles.legendBar}>
              {[
                { label: "Event", cls: styles.catEvent },
                { label: "Exam Dates", cls: styles.catExam },
                { label: "Holiday", cls: styles.catHoliday },
              ].map(t => (
                <span key={t.label} className={styles.legendItem}>
                  <i className={`${styles.legendSquare} ${t.cls}`} />
                  {t.label}
                </span>
              ))}
              {classNames.slice(1).map(c => (
                <span key={c} className={styles.legendItem}>
                  <i className={styles.legendSquare} style={{ backgroundColor: classColor(c) }} />
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* ---------- Right: view switch and the list ---------- */}
          <div className="col-lg-8">
            <div className={styles.viewBar} role="group" aria-label="Calendar view">
              <button type="button" className={styles.viewOpt} aria-pressed={view === "month"} onClick={() => setView("month")}>
                View By Month <i className={styles.check}>{view === "month" && <FiCheck />}</i>
              </button>
              <button type="button" className={styles.viewOpt} aria-pressed={view === "all"} onClick={() => setView("all")}>
                View All <i className={styles.check}>{view === "all" && <FiCheck />}</i>
              </button>
            </div>

            {loading && (
              <div className={styles.stateBox}><div className={styles.spinner} /></div>
            )}

            {!loading && error && (
              <div className={styles.stateBox}>
                <h3>Unable to load the calendar</h3>
                <p>We couldn&apos;t retrieve the school calendar right now. Please try again shortly.</p>
                <button type="button" className={styles.retryBtn} onClick={() => setRetry(r => r + 1)}>Try again</button>
              </div>
            )}

            {!loading && !error && view === "month" && (
              <div className={styles.monthBar}>
                <button type="button" aria-label="Previous month" disabled={viewMonth === 0} onClick={() => goToMonth(viewMonth - 1)}><FiArrowLeft /></button>
                <span>EVENTS IN {MONTH_NAMES[viewMonth].toUpperCase()} {year}</span>
                <button type="button" aria-label="Next month" disabled={viewMonth === 11} onClick={() => goToMonth(viewMonth + 1)}><FiArrowRight /></button>
              </div>
            )}

            {!loading && !error && activeDay !== null && (
              <button type="button" className={styles.allMonthBar} onClick={() => setSelectedDay(null)}>
                View all events for this month
              </button>
            )}

            {!loading && !error && !hasAny && (
              <div className={styles.stateBox}>
                <FiCalendar size={36} />
                <h3>Nothing scheduled</h3>
                <p>There are no calendar entries {view === "month" ? `for ${MONTH_NAMES[viewMonth]}` : `from ${MONTH_NAMES[today.getMonth()]} to December ${year}`}{category !== ALL_CATEGORIES ? ` in ${category}` : ""}{classFilter !== ALL_CLASSES ? ` for ${classFilter}` : ""}.</p>
              </div>
            )}

            {!loading && !error && groups.map(group => (
              <div key={group.key}>
                {view === "all" && <div className={styles.monthBar}><span>{group.label.toUpperCase()}</span></div>}
                {group.list.map(item => {
                  const start = parseDay(item.date);
                  const multi = isMultiDay(item);
                  const end = endOf(item);
                  const cat = CATEGORY_CLASS[categoryOf(item)] || styles.catOther;
                  const classTint = item.sourceType === "ClassEvent" ? classColor(item.className) : undefined;
                  const tint = classTint ? { backgroundColor: classTint, color: "#fff" } : undefined;
                  const time = timeLabel(item);
                  return (
                    <div key={`${item.sourceType}-${item.id}-${item.date}`}>
                      <div className={`${styles.eventRow} ${end < today ? styles.pastRow : ""}`}>
                        <div className={styles.dateBadges}>
                          <div className={`${styles.dateBadge} ${multi ? styles.dateBadgeFirst : ""} ${cat}`} style={tint}>
                            <strong>{pad2(start.getDate())}</strong>
                            <span>{MONTH_NAMES[start.getMonth()].slice(0, 3).toUpperCase()}</span>
                          </div>
                          {multi && (
                            <div className={`${styles.dateBadge} ${styles.dateBadgeEnd} ${cat}`} style={tint}>
                              <strong>{pad2(end.getDate())}</strong>
                              <span>{MONTH_NAMES[end.getMonth()].slice(0, 3).toUpperCase()}</span>
                            </div>
                          )}
                        </div>

                        <div className={styles.eventBody}>
                          <h4>{item.title}</h4>
                          <p className={styles.meta}>
                            <span>{item.className ? `${categoryOf(item)} - ${item.className}` : categoryOf(item)}</span>
                            {time && <span><FiClock /> {time}</span>}
                            {item.location && <span><FiMapPin /> {item.location}</span>}
                          </p>
                          {item.description && <p className={styles.description}>{item.description}</p>}
                        </div>

                        <button
                          type="button"
                          className={styles.addBtn}
                          title="Add to calendar"
                          aria-label={`Add ${item.title} to calendar`}
                          onClick={() => downloadIcs([item], `${item.title.replace(/[^\w]+/g, "-").toLowerCase()}.ics`)}
                        >
                          <FiCalendar />
                        </button>
                      </div>
                      <div className={styles.rowRule} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SchoolCalendar;
